import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { ApiError } from './api';
import type {
  AdminApi,
  ChecklistTemplateAdmin,
  ConfigurationImpactAssessment,
  ConfigurationImpactMode,
  DocumentTypeAdmin,
  EquipmentPackageRevision,
  EquipmentTypeAdmin,
  InspectionSiteAdmin,
} from './admin-api';
import {
  confirmAdminNavigation,
  getUnsavedLeaveMessage,
  registerAdminLeaveGuard,
} from './admin-leave-guard';
import { generateInternalCode } from './admin-internal-codes';
import { problemMessage } from './admin-presentation';
import { StatusChip, statusToneClass } from './admin-ui';
import { ReferenceDataAdmin } from './a4-admin';
import { validationAfterValueTypeChange, isEnumValidationIncomplete } from './cfg-val-001';
import { CfgValValidationEditor } from './cfg-val-001-editor';
import {
  applicabilityRuleTypeOptions,
  countMetadataFields,
  directoryStatusLabel,
  documentTypeBusinessLabel,
  emptyMetadataField,
  emptyMetadataSchema,
  equipmentTypeBusinessLabel,
  impactAssessmentSummary,
  impactModeLabel,
  inspectionSiteBusinessLabel,
  isSimpleApplicabilityRule,
  METADATA_VALUE_TYPE_OPTIONS,
  metadataSchemaFromServer,
  nextDisplayOrder,
  packageRevisionBusinessLabel,
  reindexMetadataOrdinals,
  requiredRuleTypeOptions,
  runtimeReadinessLabel,
  runtimeReadinessReasonLabels,
  schemaFromMetadataDraft,
  type MetadataFieldDraft,
  type MetadataSchemaDraft,
} from './configuration-catalog-presentation';
import { navigate } from './path-router';
import { templateBusinessLabel } from './template-admin-presentation';

type VisibleSection = 'document-types' | 'equipment-types' | 'inspection-sites';

const VISIBLE_TABS: readonly { readonly id: VisibleSection; readonly label: string }[] = [
  { id: 'document-types', label: 'Типы документов' },
  { id: 'equipment-types', label: 'Виды ТС / спецтехники' },
  { id: 'inspection-sites', label: 'Места / площадки осмотра' },
];

const PAGE_SIZE = 10;
const STALE_UI_MESSAGE =
  'Эту запись изменили после открытия. Обновите данные и повторите сохранение';

type CatalogRoute =
  | { readonly kind: 'list'; readonly section: VisibleSection }
  | { readonly kind: 'new'; readonly section: VisibleSection }
  | { readonly kind: 'detail'; readonly section: VisibleSection; readonly id: string }
  | { readonly kind: 'package'; readonly equipmentTypeId: string }
  | { readonly kind: 'datasets' };

type StatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED';

type FilterState = { query: string; status: StatusFilter };

const filterSessions: Record<VisibleSection, { draft: FilterState; applied: FilterState }> = {
  'document-types': {
    draft: { query: '', status: 'ALL' },
    applied: { query: '', status: 'ALL' },
  },
  'equipment-types': {
    draft: { query: '', status: 'ALL' },
    applied: { query: '', status: 'ALL' },
  },
  'inspection-sites': {
    draft: { query: '', status: 'ALL' },
    applied: { query: '', status: 'ALL' },
  },
};

function parseCatalogRoute(path: string): CatalogRoute {
  if (path === '/admin/reference-data/datasets' || path.startsWith('/admin/reference-data/datasets/')) {
    return { kind: 'datasets' };
  }
  const base = '/admin/reference-data';
  if (path === base || path === `${base}/`) {
    return { kind: 'list', section: 'document-types' };
  }
  for (const section of VISIBLE_TABS.map((tab) => tab.id)) {
    const prefix = `${base}/${section}`;
    if (path === prefix) {
      return { kind: 'list', section };
    }
    if (path === `${prefix}/new`) {
      return { kind: 'new', section };
    }
    if (section === 'equipment-types') {
      const packageMatch = path.match(
        /^\/admin\/reference-data\/equipment-types\/([^/]+)\/package$/,
      );
      if (packageMatch?.[1]) {
        return { kind: 'package', equipmentTypeId: packageMatch[1] };
      }
    }
    if (path.startsWith(`${prefix}/`)) {
      const id = path.slice(prefix.length + 1);
      if (id.length > 0 && !id.includes('/')) {
        return { kind: 'detail', section, id };
      }
    }
  }
  return { kind: 'list', section: 'document-types' };
}

function useUnsavedLeaveGuard(isDirty: boolean): void {
  useEffect(() => registerAdminLeaveGuard(() => isDirty), [isDirty]);
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = getUnsavedLeaveMessage();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);
}

function guardedNavigate(path: string, mode: 'push' | 'replace' = 'push'): void {
  if (!confirmAdminNavigation()) {
    return;
  }
  navigate(path, mode);
}

function isPlainPrimaryClick(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

function CatalogLink(props: { readonly href: string; readonly children: ReactNode }): ReactElement {
  return (
    <a
      href={props.href}
      className="pkg-b-registry-link"
      onClick={(event) => {
        if (!isPlainPrimaryClick(event)) {
          return;
        }
        event.preventDefault();
        if (!confirmAdminNavigation()) {
          return;
        }
        navigate(props.href);
      }}
    >
      {props.children}
    </a>
  );
}

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  return (
    <div role="alert" className="banner banner-closed">
      <p>{typeof props.failure === 'string' ? props.failure : problemMessage(props.failure)}</p>
    </div>
  );
}

function compareRu(left: string, right: string): number {
  return left.localeCompare(right, 'ru', { sensitivity: 'base', numeric: true });
}

function matchIncludes(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase('ru').includes(needle.trim().toLocaleLowerCase('ru'));
}

function paginate<T>(rows: readonly T[], page: number): { pageRows: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  return { pageRows: rows.slice(start, start + PAGE_SIZE) as T[], totalPages };
}

export function ConfigurationCatalogAdmin(props: {
  readonly api: AdminApi;
  readonly path: string;
}): ReactElement {
  const route = parseCatalogRoute(props.path);

  useEffect(() => {
    if (props.path === '/admin/reference-data' || props.path === '/admin/reference-data/') {
      navigate('/admin/reference-data/document-types', 'replace');
    }
  }, [props.path]);

  if (route.kind === 'datasets') {
    return (
      <div className="pkg-b-page">
        <button
          type="button"
          className="back-link"
          onClick={() => guardedNavigate('/admin/reference-data/document-types')}
        >
          ← К справочникам
        </button>
        <h1>Произвольные наборы</h1>
        <p className="page-description">
          Экспертный раздел. Обычная работа со справочниками — через вкладки типов документов, видов
          техники и площадок.
        </p>
        <ReferenceDataAdmin api={props.api} />
      </div>
    );
  }

  const activeSection: VisibleSection =
    route.kind === 'package' ? 'equipment-types' : route.section;

  return (
    <div className="pkg-b-page">
      <div className="pkg-b-toolbar">
        <div>
          <h1>Справочники</h1>
          <p className="page-description">
            Типы документов, виды техники и площадки осмотра. Чек-листы и экземпляры ТС ведутся в
            отдельных разделах.
          </p>
        </div>
      </div>
      <div className="section-tabs pkg-b-tabs" role="tablist" aria-label="Разделы справочников">
        {VISIBLE_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={activeSection === item.id}
            className={
              activeSection === item.id
                ? 'section-tab is-active nav-link nav-current'
                : 'section-tab nav-link'
            }
            onClick={() => guardedNavigate(`/admin/reference-data/${item.id}`)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {route.kind === 'list' && route.section === 'document-types' ? (
        <DocumentTypeList api={props.api} />
      ) : null}
      {route.kind === 'new' && route.section === 'document-types' ? (
        <DocumentTypeCreate api={props.api} />
      ) : null}
      {route.kind === 'detail' && route.section === 'document-types' ? (
        <DocumentTypeDetail api={props.api} documentTypeId={route.id} />
      ) : null}
      {route.kind === 'list' && route.section === 'equipment-types' ? (
        <EquipmentTypeList api={props.api} />
      ) : null}
      {route.kind === 'new' && route.section === 'equipment-types' ? (
        <EquipmentTypeCreate api={props.api} />
      ) : null}
      {route.kind === 'detail' && route.section === 'equipment-types' ? (
        <EquipmentTypeDetail api={props.api} equipmentTypeId={route.id} />
      ) : null}
      {route.kind === 'package' ? (
        <EquipmentPackagePage api={props.api} equipmentTypeId={route.equipmentTypeId} />
      ) : null}
      {route.kind === 'list' && route.section === 'inspection-sites' ? (
        <InspectionSiteList api={props.api} />
      ) : null}
      {route.kind === 'new' && route.section === 'inspection-sites' ? (
        <InspectionSiteCreate api={props.api} />
      ) : null}
      {route.kind === 'detail' && route.section === 'inspection-sites' ? (
        <InspectionSiteDetail api={props.api} inspectionSiteId={route.id} />
      ) : null}
    </div>
  );
}

function FilterBar(props: {
  readonly section: VisibleSection;
  readonly draft: FilterState;
  readonly onDraft: (next: FilterState) => void;
  readonly onApply: () => void;
  readonly onReset: () => void;
}): ReactElement {
  return (
    <form
      className="pkg-b-filter-bar"
      onSubmit={(event) => {
        event.preventDefault();
        props.onApply();
      }}
    >
      <div className="pkg-b-filter-fields">
        <label htmlFor={`${props.section}_query`} className="pkg-b-filter-query">
          Поиск
          <input
            id={`${props.section}_query`}
            value={props.draft.query}
            onChange={(event) => props.onDraft({ ...props.draft, query: event.target.value })}
          />
        </label>
        <label htmlFor={`${props.section}_status`}>
          Статус
          <select
            id={`${props.section}_status`}
            value={props.draft.status}
            onChange={(event) =>
              props.onDraft({ ...props.draft, status: event.target.value as StatusFilter })
            }
          >
            <option value="ALL">Все</option>
            <option value="ACTIVE">Активные</option>
            <option value="ARCHIVED">В архиве</option>
          </select>
        </label>
      </div>
      <div className="pkg-b-filter-actions">
        <button type="submit">Найти</button>
        <button type="button" className="btn-ghost" onClick={props.onReset}>
          Сбросить
        </button>
      </div>
    </form>
  );
}

function RegistryStates(props: {
  readonly loading: boolean;
  readonly error: unknown;
  readonly empty: boolean;
  readonly filteredEmpty: boolean;
  readonly onRetry: () => void;
}): ReactElement | null {
  if (props.loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (props.error !== null) {
    return (
      <div role="alert" className="banner banner-closed">
        <p>Не удалось загрузить данные</p>
        <ProblemAlert failure={props.error} />
        <button type="button" onClick={props.onRetry}>
          Повторить
        </button>
      </div>
    );
  }
  if (props.empty) {
    return <p role="status">Записей пока нет</p>;
  }
  if (props.filteredEmpty) {
    return <p role="status">По выбранным фильтрам ничего не найдено</p>;
  }
  return null;
}

function FormActions(props: {
  readonly primaryLabel: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly cancelLabel?: string;
  readonly disabled?: boolean;
  readonly archiveLabel?: string;
  readonly onArchive?: () => void;
  readonly archiveBusy?: boolean;
}): ReactElement {
  return (
    <div className="pkg-b-form-actions">
      <button type="submit" disabled={props.busy || props.disabled === true}>
        {props.busy ? 'Сохранение…' : props.primaryLabel}
      </button>
      <button type="button" className="btn-ghost" disabled={props.busy} onClick={props.onCancel}>
        {props.cancelLabel ?? 'Отменить'}
      </button>
      {props.onArchive !== undefined && props.archiveLabel !== undefined ? (
        <button
          type="button"
          className="btn-ghost pkg-b-archive-action"
          disabled={props.busy || props.archiveBusy === true}
          onClick={props.onArchive}
        >
          {props.archiveLabel}
        </button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Document types                                                             */
/* -------------------------------------------------------------------------- */

function DocumentTypeList(props: { readonly api: AdminApi }): ReactElement {
  const session = filterSessions['document-types'];
  const [draft, setDraft] = useState<FilterState>(session.draft);
  const [applied, setApplied] = useState<FilterState>(session.applied);
  const [rows, setRows] = useState<DocumentTypeAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [page, setPage] = useState(1);

  const reload = () => {
    setLoading(true);
    void props.api
      .listDocumentTypes({
        search: applied.query.trim() || undefined,
        status: applied.status === 'ALL' ? undefined : applied.status,
      })
      .then((items) => {
        setRows(Array.isArray(items) ? items : []);
        setError(null);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, applied.query, applied.status]);

  const filtered = rows
    .filter((row) => {
      if (applied.status !== 'ALL' && row.status !== applied.status) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(row.display_name, applied.query) ||
        matchIncludes(row.description ?? '', applied.query)
      );
    })
    .slice()
    .sort((a, b) => {
      const byName = compareRu(a.display_name, b.display_name);
      return byName !== 0 ? byName : compareRu(a.id, b.id);
    });
  const { pageRows, totalPages } = paginate(filtered, page);

  return (
    <div className="pkg-b-section">
      <div className="pkg-b-section-head">
        <h2>Типы документов</h2>
        <button
          type="button"
          className="pkg-b-create"
          onClick={() => guardedNavigate('/admin/reference-data/document-types/new')}
        >
          Создать тип документа
        </button>
      </div>
      <FilterBar
        section="document-types"
        draft={draft}
        onDraft={(next) => {
          session.draft = next;
          setDraft(next);
        }}
        onApply={() => {
          session.applied = draft;
          session.draft = draft;
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          const empty: FilterState = { query: '', status: 'ALL' };
          session.draft = empty;
          session.applied = empty;
          setDraft(empty);
          setApplied(empty);
          setPage(1);
        }}
      />
      <RegistryStates
        loading={loading}
        error={error}
        empty={!loading && error === null && rows.length === 0}
        filteredEmpty={!loading && error === null && rows.length > 0 && filtered.length === 0}
        onRetry={reload}
      />
      {!loading && error === null && filtered.length > 0 ? (
        <>
          <p className="meta pkg-b-tablebar">
            Показано {pageRows.length} из {filtered.length}
          </p>
          <div className="table-wrap">
            <table className="data-table pkg-b-table">
              <thead>
                <tr>
                  <th scope="col">Наименование</th>
                  <th scope="col">Реквизиты</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Действие</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <CatalogLink href={`/admin/reference-data/document-types/${row.id}`}>
                        {documentTypeBusinessLabel(row)}
                      </CatalogLink>
                    </td>
                    <td>{countMetadataFields(row.default_metadata_schema)}</td>
                    <td>
                      <StatusChip
                        label={directoryStatusLabel(row.status)}
                        tone={statusToneClass(row.status)}
                      />
                    </td>
                    <td>
                      <CatalogLink href={`/admin/reference-data/document-types/${row.id}`}>
                        Открыть
                      </CatalogLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="pkg-b-pager">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Назад
              </button>
              <span className="meta">
                Стр. {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

type CreatedDocumentAnchor = {
  readonly id: string;
  readonly version: string;
};

function DocumentTypeCreate(props: { readonly api: AdminApi }): ReactElement {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [metadataDraft, setMetadataDraft] = useState<MetadataSchemaDraft>({
    fields: [],
    preservedRoot: {},
  });
  const [createdRecord, setCreatedRecord] = useState<CreatedDocumentAnchor | null>(null);
  const [createUncertain, setCreateUncertain] = useState(false);
  const [schemaStale, setSchemaStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submitSeq = useRef(0);
  const submitInFlight = useRef(false);
  const dirty =
    displayName.trim().length > 0 ||
    description.trim().length > 0 ||
    metadataDraft.fields.length > 0 ||
    createdRecord !== null;
  useUnsavedLeaveGuard(dirty && !busy);

  const saveSchemaForCreated = async (anchor: CreatedDocumentAnchor): Promise<DocumentTypeAdmin> => {
    return props.api.updateDocumentType(anchor.id, {
      expected_version: anchor.version,
      default_metadata_schema: schemaFromMetadataDraft(metadataDraft),
      description: description.trim().length === 0 ? null : description.trim(),
      display_name: displayName.trim(),
    });
  };

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/document-types')}
      >
        ← К типам документов
      </button>
      <h2>Новый тип документа</h2>
      <p className="meta">
        Сначала создаётся запись, затем при наличии реквизитов выполняется отдельное сохранение
        схемы.
      </p>
      {createdRecord !== null ? (
        <div className="banner banner-open" role="status">
          <p>
            Запись уже создана на сервере. Повторное нажатие сохранит только реквизиты этой же
            записи — новый тип документа не создаётся.
          </p>
          <div className="pkg-b-form-actions">
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() =>
                guardedNavigate(`/admin/reference-data/document-types/${createdRecord.id}`)
              }
            >
              Открыть карточку
            </button>
          </div>
        </div>
      ) : null}
      {createUncertain ? (
        <div className="banner banner-closed" role="alert">
          <p>
            Не удалось подтвердить результат создания. Запись могла уже появиться на сервере.
            Откройте список типов документов и проверьте, прежде чем создавать снова.
          </p>
          <div className="pkg-b-form-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => guardedNavigate('/admin/reference-data/document-types')}
            >
              К списку типов документов
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() => {
                if (
                  !window.confirm(
                    'Вы проверили список и уверены, что запись не создана? Повтор создаст новый тип документа.',
                  )
                ) {
                  return;
                }
                setCreateUncertain(false);
                setError(null);
              }}
            >
              Я проверил — разрешить создание
            </button>
          </div>
        </div>
      ) : null}
      {schemaStale ? (
        <div className="banner banner-closed" role="alert">
          <p>{STALE_UI_MESSAGE}</p>
          <button
            type="button"
            disabled={busy || createdRecord === null}
            onClick={() => {
              if (createdRecord === null || busy) {
                return;
              }
              const seq = ++submitSeq.current;
              setBusy(true);
              setError(null);
              void props.api
                .getDocumentType(createdRecord.id)
                .then((row) => {
                  if (seq !== submitSeq.current) {
                    return;
                  }
                  setCreatedRecord({ id: row.id, version: row.version });
                  setSchemaStale(false);
                  setBusy(false);
                })
                .catch((failure: unknown) => {
                  if (seq !== submitSeq.current) {
                    return;
                  }
                  setError(failure);
                  setBusy(false);
                });
            }}
          >
            Обновить версию с сервера
          </button>
        </div>
      ) : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || submitInFlight.current) {
            return;
          }
          if (createUncertain && createdRecord === null) {
            setError(
              'Сначала проверьте список типов документов. Повторное создание без проверки может создать дубликат.',
            );
            return;
          }
          if (
            metadataDraft.fields.some((row) =>
              isEnumValidationIncomplete(row.value_type, row.validation),
            )
          ) {
            setError(
              'Добавьте хотя бы одно непустое разрешённое значение для полей типа ENUM',
            );
            return;
          }
          const seq = ++submitSeq.current;
          submitInFlight.current = true;
          setBusy(true);
          setError(null);
          void (async () => {
            let anchor: CreatedDocumentAnchor | null = createdRecord;
            try {
              if (anchor === null) {
                const created = await props.api.createDocumentType({
                  code: generateInternalCode('DocumentType'),
                  display_name: displayName.trim(),
                  description: description.trim().length === 0 ? null : description.trim(),
                  default_metadata_schema: emptyMetadataSchema(),
                });
                if (seq !== submitSeq.current) {
                  submitInFlight.current = false;
                  return;
                }
                anchor = { id: created.id, version: created.version };
                setCreatedRecord(anchor);
                setCreateUncertain(false);
                if (metadataDraft.fields.length === 0) {
                  submitInFlight.current = false;
                  setBusy(false);
                  navigate(`/admin/reference-data/document-types/${created.id}`);
                  return;
                }
              }
              if (metadataDraft.fields.length === 0) {
                if (seq !== submitSeq.current) {
                  submitInFlight.current = false;
                  return;
                }
                submitInFlight.current = false;
                setBusy(false);
                navigate(`/admin/reference-data/document-types/${anchor.id}`);
                return;
              }
              const updated = await saveSchemaForCreated(anchor);
              if (seq !== submitSeq.current) {
                submitInFlight.current = false;
                return;
              }
              setCreatedRecord({ id: updated.id, version: updated.version });
              setSchemaStale(false);
              submitInFlight.current = false;
              setBusy(false);
              navigate(`/admin/reference-data/document-types/${updated.id}`);
            } catch (failure: unknown) {
              if (seq !== submitSeq.current) {
                submitInFlight.current = false;
                return;
              }
              if (anchor === null) {
                if (!(failure instanceof ApiError)) {
                  setCreateUncertain(true);
                  // Banner above explains uncertainty; avoid duplicate ProblemAlert text.
                  setError(null);
                } else {
                  setError(failure);
                }
              } else {
                setCreatedRecord(anchor);
                if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                  setSchemaStale(true);
                }
                setError(failure);
              }
              submitInFlight.current = false;
              setBusy(false);
            }
          })();
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="doc_create_name">
            Название типа документа
            <input
              id="doc_create_name"
              aria-label="Название типа документа"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
              disabled={busy}
            />
          </label>
          <label htmlFor="doc_create_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="doc_create_desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              disabled={busy}
            />
          </label>
        </div>
        <section className="pkg-b-fieldset" aria-labelledby="doc-create-fields-title">
          <h3 id="doc-create-fields-title">Реквизиты документа</h3>
          <MetadataFieldsEditor draft={metadataDraft} onChange={setMetadataDraft} />
        </section>
        <FormActions
          primaryLabel={createdRecord !== null ? 'Сохранить реквизиты' : 'Создать'}
          busy={busy}
          onCancel={() => guardedNavigate('/admin/reference-data/document-types')}
        />
      </form>
    </div>
  );
}

function DocumentTypeDetail(props: {
  readonly api: AdminApi;
  readonly documentTypeId: string;
}): ReactElement {
  const [row, setRow] = useState<DocumentTypeAdmin | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [metadataDraft, setMetadataDraft] = useState<MetadataSchemaDraft>({
    fields: [],
    preservedRoot: {},
  });
  const [baseline, setBaseline] = useState<{
    name: string;
    description: string;
    draft: MetadataSchemaDraft;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const loadSeq = useRef(0);

  const apply = (next: DocumentTypeAdmin) => {
    const draft = metadataSchemaFromServer(next.default_metadata_schema);
    setRow(next);
    setDisplayName(next.display_name);
    setDescription(next.description ?? '');
    setMetadataDraft(draft);
    setBaseline({ name: next.display_name, description: next.description ?? '', draft });
    setStale(false);
  };

  const reload = () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    void props.api
      .getDocumentType(props.documentTypeId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        apply(next);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (failure instanceof ApiError && failure.status === 404) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.documentTypeId]);

  const dirty =
    baseline !== null &&
    (displayName !== baseline.name ||
      description !== baseline.description ||
      JSON.stringify(metadataDraft) !== JSON.stringify(baseline.draft));
  useUnsavedLeaveGuard(dirty && !busy);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return (
      <div role="alert">
        <p>Запись не найдена</p>
        <button
          type="button"
          className="back-link"
          onClick={() => navigate('/admin/reference-data/document-types')}
        >
          К списку
        </button>
      </div>
    );
  }
  if (row === null) {
    return error !== null ? <ProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/document-types')}
      >
        ← К типам документов
      </button>
      <div className="pkg-b-detail-header">
        <h2>{documentTypeBusinessLabel(row)}</h2>
        <StatusChip label={directoryStatusLabel(row.status)} tone={statusToneClass(row.status)} />
      </div>
      {saved ? (
        <div role="status" className="banner banner-open">
          <p>Изменения сохранены</p>
        </div>
      ) : null}
      {stale ? (
        <div role="alert" className="banner banner-closed">
          <p>{STALE_UI_MESSAGE}</p>
          <button type="button" onClick={reload}>
            Перезагрузить актуальное
          </button>
        </div>
      ) : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          if (
            metadataDraft.fields.some((field) =>
              isEnumValidationIncomplete(field.value_type, field.validation),
            )
          ) {
            setError(
              new Error('Добавьте хотя бы одно непустое разрешённое значение для полей типа ENUM'),
            );
            return;
          }
          const targetId = row.id;
          const expected = row.version;
          const seq = loadSeq.current;
          setBusy(true);
          setError(null);
          setSaved(false);
          void props.api
            .updateDocumentType(targetId, {
              expected_version: expected,
              display_name: displayName.trim(),
              description: description.trim().length === 0 ? null : description.trim(),
              default_metadata_schema: schemaFromMetadataDraft(metadataDraft),
            })
            .then((next) => {
              if (loadSeq.current !== seq) {
                return;
              }
              apply(next);
              setSaved(true);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              if (loadSeq.current !== seq) {
                return;
              }
              setBusy(false);
              if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                setStale(true);
                setError(failure);
                return;
              }
              setError(failure);
            });
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="doc_edit_name">
            Название типа документа для сохранения
            <input
              id="doc_edit_name"
              aria-label="Название типа документа для сохранения"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label htmlFor="doc_edit_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="doc_edit_desc"
              aria-label="Описание типа документа"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>
        </div>
        <section className="pkg-b-fieldset" aria-labelledby="doc-edit-fields-title">
          <h3 id="doc-edit-fields-title">Реквизиты документа</h3>
          <MetadataFieldsEditor draft={metadataDraft} onChange={setMetadataDraft} />
        </section>
        <FormActions
          primaryLabel="Сохранить"
          busy={busy}
          cancelLabel="Отменить"
          onCancel={() => {
            if (baseline === null) {
              return;
            }
            if (dirty && !window.confirm(getUnsavedLeaveMessage())) {
              return;
            }
            setDisplayName(baseline.name);
            setDescription(baseline.description);
            setMetadataDraft(baseline.draft);
            setError(null);
            setSaved(false);
          }}
          archiveLabel={row.status === 'ACTIVE' ? 'В архив' : undefined}
          archiveBusy={archiveBusy}
          onArchive={
            row.status === 'ACTIVE'
              ? () => {
                  if (
                    !window.confirm(
                      `Перенести «${documentTypeBusinessLabel(row)}» в архив? Запись останется в истории.`,
                    )
                  ) {
                    return;
                  }
                  const targetId = row.id;
                  const expected = row.version;
                  const seq = loadSeq.current;
                  setArchiveBusy(true);
                  setError(null);
                  void props.api
                    .archiveDocumentType(targetId, expected)
                    .then((next) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      apply(next);
                      setArchiveBusy(false);
                    })
                    .catch((failure: unknown) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      setArchiveBusy(false);
                      if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                        setStale(true);
                      }
                      setError(failure);
                    });
                }
              : undefined
          }
        />
      </form>
    </div>
  );
}

function MetadataFieldsEditor(props: {
  readonly draft: MetadataSchemaDraft;
  readonly onChange: (next: MetadataSchemaDraft) => void;
}): ReactElement {
  const replaceFields = (fields: MetadataFieldDraft[]) => {
    props.onChange({ ...props.draft, fields });
  };
  const update = (index: number, patch: Partial<MetadataFieldDraft>) => {
    replaceFields(
      props.draft.fields.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    );
  };
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= props.draft.fields.length) {
      return;
    }
    const next = [...props.draft.fields];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row!);
    replaceFields(reindexMetadataOrdinals(next));
  };
  const removeAt = (index: number) => {
    replaceFields(
      reindexMetadataOrdinals(props.draft.fields.filter((_, rowIndex) => rowIndex !== index)),
    );
  };

  return (
    <div className="stack-gap">
      {props.draft.fields.map((row, index) => {
        const requiredType =
          typeof row.required_rule.type === 'string' ? row.required_rule.type : 'OPTIONAL';
        const applicabilityType =
          typeof row.applicability_rule.type === 'string' ? row.applicability_rule.type : 'ALWAYS';
        const simpleApplicability = isSimpleApplicabilityRule(row.applicability_rule);
        return (
          <div key={`${row.field_code}-${index}`} className="pkg-b-field-card">
            <p className="meta">Порядок: {row.ordinal}</p>
            <label>
              Подпись
              <input
                aria-label={`Подпись поля метаданных ${index + 1}`}
                value={row.label}
                onChange={(event) => update(index, { label: event.target.value })}
              />
            </label>
            <label>
              Тип значения
              <select
                aria-label={`Тип значения поля метаданных ${index + 1}`}
                value={row.value_type}
                onChange={(event) => {
                  const nextType = event.target.value;
                  const result = validationAfterValueTypeChange(row.validation, nextType);
                  if (result.blocked) {
                    window.alert(
                      'Сначала преобразуйте историческое правило проверки в Validation v1, затем меняйте тип значения.',
                    );
                    return;
                  }
                  update(index, { value_type: nextType, validation: result.validation });
                }}
              >
                {METADATA_VALUE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Обязательность
              <select
                aria-label={`Обязательность поля метаданных ${index + 1}`}
                value={requiredType}
                onChange={(event) =>
                  update(index, { required_rule: { version: 1, type: event.target.value } })
                }
              >
                {requiredRuleTypeOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {simpleApplicability ? (
              <label>
                Применимость
                <select
                  aria-label={`Применимость поля метаданных ${index + 1}`}
                  value={applicabilityType}
                  onChange={(event) =>
                    update(index, {
                      applicability_rule: { version: 1, type: event.target.value },
                    })
                  }
                >
                  {applicabilityRuleTypeOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="meta" role="status">
                Сложное правило применимости сохранено без изменений.
              </p>
            )}
            <label>
              Подсказка
              <input
                aria-label={`Подсказка поля метаданных ${index + 1}`}
                value={row.help_text}
                onChange={(event) => update(index, { help_text: event.target.value })}
              />
            </label>
            <div>
              <p className="meta">Правила проверки</p>
              <CfgValValidationEditor
                valueType={row.value_type}
                validation={row.validation}
                ariaPrefix={`поле метаданных ${index + 1}`}
                onChange={(next) => update(index, { validation: next })}
              />
            </div>
            <div className="pkg-b-form-actions">
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0}>
                Вверх
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === props.draft.fields.length - 1}
              >
                Вниз
              </button>
              <button type="button" className="btn-ghost" onClick={() => removeAt(index)}>
                Удалить
              </button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() =>
          replaceFields(
            reindexMetadataOrdinals([
              ...props.draft.fields,
              emptyMetadataField(props.draft.fields.length + 1),
            ]),
          )
        }
      >
        Добавить реквизит
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Equipment types                                                            */
/* -------------------------------------------------------------------------- */

function EquipmentTypeList(props: { readonly api: AdminApi }): ReactElement {
  const session = filterSessions['equipment-types'];
  const [draft, setDraft] = useState<FilterState>(session.draft);
  const [applied, setApplied] = useState<FilterState>(session.applied);
  const [rows, setRows] = useState<EquipmentTypeAdmin[]>([]);
  const [packageCounts, setPackageCounts] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [page, setPage] = useState(1);

  const reload = () => {
    setLoading(true);
    void props.api
      .listEquipmentTypes()
      .then((items) => {
        setRows(Array.isArray(items) ? items : []);
        setError(null);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const filtered = rows
    .filter((row) => {
      if (applied.status !== 'ALL' && row.status !== applied.status) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(row.display_name, applied.query) ||
        matchIncludes(row.description ?? '', applied.query)
      );
    })
    .slice()
    .sort((a, b) => {
      const byOrder = a.display_order - b.display_order;
      return byOrder !== 0 ? byOrder : compareRu(a.display_name, b.display_name);
    });
  const { pageRows, totalPages } = paginate(filtered, page);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      const next: Record<string, number | null> = {};
      await Promise.all(
        pageRows.map(async (row) => {
          if (row.current_package_revision_id === null) {
            next[row.id] = null;
            return;
          }
          try {
            const revision = await props.api.getEquipmentPackageRevision(
              row.id,
              row.current_package_revision_id,
            );
            next[row.id] = Array.isArray(revision.items) ? revision.items.length : 0;
          } catch {
            next[row.id] = null;
          }
        }),
      );
      if (!cancelled) {
        setPackageCounts((prev) => ({ ...prev, ...next }));
      }
    };
    void loadCounts();
    return () => {
      cancelled = true;
    };
  }, [props.api, pageRows.map((row) => row.id).join('|')]);

  return (
    <div className="pkg-b-section">
      <div className="pkg-b-section-head">
        <h2>Виды ТС / спецтехники</h2>
        <button
          type="button"
          className="pkg-b-create"
          onClick={() => guardedNavigate('/admin/reference-data/equipment-types/new')}
        >
          Создать вид техники
        </button>
      </div>
      <FilterBar
        section="equipment-types"
        draft={draft}
        onDraft={(next) => {
          session.draft = next;
          setDraft(next);
        }}
        onApply={() => {
          session.applied = draft;
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          const empty: FilterState = { query: '', status: 'ALL' };
          session.draft = empty;
          session.applied = empty;
          setDraft(empty);
          setApplied(empty);
          setPage(1);
        }}
      />
      <RegistryStates
        loading={loading}
        error={error}
        empty={!loading && error === null && rows.length === 0}
        filteredEmpty={!loading && error === null && rows.length > 0 && filtered.length === 0}
        onRetry={reload}
      />
      {!loading && error === null && filtered.length > 0 ? (
        <>
          <p className="meta pkg-b-tablebar">
            Показано {pageRows.length} из {filtered.length}
          </p>
          <div className="table-wrap">
            <table className="data-table pkg-b-table">
              <thead>
                <tr>
                  <th scope="col">Вид техники</th>
                  <th scope="col">Порядок</th>
                  <th scope="col">Чек-листы</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Действие</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const count = packageCounts[row.id];
                  const checklistsLabel =
                    row.current_package_revision_id === null || count === null
                      ? 'Не настроено'
                      : String(count);
                  return (
                    <tr key={row.id}>
                      <td>
                        <CatalogLink href={`/admin/reference-data/equipment-types/${row.id}`}>
                          {equipmentTypeBusinessLabel(row)}
                        </CatalogLink>
                      </td>
                      <td>{row.display_order}</td>
                      <td>{checklistsLabel}</td>
                      <td>
                        <StatusChip
                          label={directoryStatusLabel(row.status)}
                          tone={statusToneClass(row.status)}
                        />
                      </td>
                      <td>
                        <CatalogLink href={`/admin/reference-data/equipment-types/${row.id}`}>
                          Открыть
                        </CatalogLink>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="pkg-b-pager">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Назад
              </button>
              <span className="meta">
                Стр. {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function EquipmentTypeCreate(props: { readonly api: AdminApi }): ReactElement {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [existing, setExisting] = useState<EquipmentTypeAdmin[]>([]);
  useUnsavedLeaveGuard(
    (displayName.trim().length > 0 || description.trim().length > 0) && !busy,
  );

  useEffect(() => {
    void props.api
      .listEquipmentTypes()
      .then((rows) => setExisting(Array.isArray(rows) ? rows : []))
      .catch(() => setExisting([]));
  }, [props.api]);

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/equipment-types')}
      >
        ← К видам техники
      </button>
      <h2>Новый вид техники</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          setBusy(true);
          setError(null);
          void props.api
            .createEquipmentType({
              code: generateInternalCode('EquipmentType'),
              display_name: displayName.trim(),
              description: description.trim().length === 0 ? null : description.trim(),
              display_order: nextDisplayOrder(existing),
            })
            .then((row) => {
              setBusy(false);
              navigate(`/admin/reference-data/equipment-types/${row.id}`);
            })
            .catch((failure: unknown) => {
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="eq_create_name">
            Название типа спецтехники
            <input
              id="eq_create_name"
              aria-label="Название типа спецтехники"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label htmlFor="eq_create_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="eq_create_desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>
        </div>
        <FormActions
          primaryLabel="Создать"
          busy={busy}
          onCancel={() => guardedNavigate('/admin/reference-data/equipment-types')}
        />
      </form>
    </div>
  );
}

function EquipmentTypeDetail(props: {
  readonly api: AdminApi;
  readonly equipmentTypeId: string;
}): ReactElement {
  const [row, setRow] = useState<EquipmentTypeAdmin | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [baseline, setBaseline] = useState<{
    name: string;
    description: string;
    order: string;
  } | null>(null);
  const [currentRevision, setCurrentRevision] = useState<EquipmentPackageRevision | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const loadSeq = useRef(0);

  const apply = (next: EquipmentTypeAdmin) => {
    setRow(next);
    setDisplayName(next.display_name);
    setDescription(next.description ?? '');
    setDisplayOrder(String(next.display_order));
    setBaseline({
      name: next.display_name,
      description: next.description ?? '',
      order: String(next.display_order),
    });
    setStale(false);
  };

  const reload = () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    void props.api
      .getEquipmentType(props.equipmentTypeId)
      .then(async (next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        apply(next);
        if (next.current_package_revision_id !== null) {
          try {
            const revision = await props.api.getEquipmentPackageRevision(
              next.id,
              next.current_package_revision_id,
            );
            if (loadSeq.current === seq) {
              setCurrentRevision(revision);
            }
          } catch {
            if (loadSeq.current === seq) {
              setCurrentRevision(null);
            }
          }
        } else {
          setCurrentRevision(null);
        }
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (failure instanceof ApiError && failure.status === 404) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.equipmentTypeId]);

  const dirty =
    baseline !== null &&
    (displayName !== baseline.name ||
      description !== baseline.description ||
      displayOrder !== baseline.order);
  useUnsavedLeaveGuard(dirty && !busy);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return (
      <div role="alert">
        <p>Запись не найдена</p>
        <button
          type="button"
          className="back-link"
          onClick={() => navigate('/admin/reference-data/equipment-types')}
        >
          К списку
        </button>
      </div>
    );
  }
  if (row === null) {
    return error !== null ? <ProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/equipment-types')}
      >
        ← К видам техники
      </button>
      <div className="pkg-b-detail-header">
        <h2>{equipmentTypeBusinessLabel(row)}</h2>
        <StatusChip label={directoryStatusLabel(row.status)} tone={statusToneClass(row.status)} />
      </div>
      <p className="meta">{runtimeReadinessLabel(row)}</p>
      {runtimeReadinessReasonLabels(row).map((reason) => (
        <p key={reason} className="meta">
          {reason}
        </p>
      ))}
      {stale ? (
        <div role="alert" className="banner banner-closed">
          <p>{STALE_UI_MESSAGE}</p>
          <button type="button" onClick={reload}>
            Перезагрузить актуальное
          </button>
        </div>
      ) : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          const targetId = row.id;
          const expected = row.version;
          const seq = loadSeq.current;
          setBusy(true);
          setError(null);
          void props.api
            .updateEquipmentType(targetId, {
              expected_version: expected,
              display_name: displayName.trim(),
              description: description.trim().length === 0 ? null : description.trim(),
              display_order: Number(displayOrder),
            })
            .then((next) => {
              if (loadSeq.current !== seq) {
                return;
              }
              apply(next);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              if (loadSeq.current !== seq) {
                return;
              }
              setBusy(false);
              if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                setStale(true);
              }
              setError(failure);
            });
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="eq_edit_name">
            Название вида техники для сохранения
            <input
              id="eq_edit_name"
              aria-label="Название вида техники для сохранения"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label htmlFor="eq_edit_order">
            Порядок отображения
            <input
              id="eq_edit_order"
              aria-label="Порядок вида техники для сохранения"
              type="number"
              min={1}
              value={displayOrder}
              onChange={(event) => setDisplayOrder(event.target.value)}
              required
            />
          </label>
          <label htmlFor="eq_edit_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="eq_edit_desc"
              aria-label="Описание вида техники"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>
        </div>
        <FormActions
          primaryLabel="Сохранить"
          busy={busy}
          onCancel={() => {
            if (baseline === null) {
              return;
            }
            if (dirty && !window.confirm(getUnsavedLeaveMessage())) {
              return;
            }
            setDisplayName(baseline.name);
            setDescription(baseline.description);
            setDisplayOrder(baseline.order);
            setError(null);
          }}
          archiveLabel={row.status === 'ACTIVE' ? 'В архив' : undefined}
          archiveBusy={archiveBusy}
          onArchive={
            row.status === 'ACTIVE'
              ? () => {
                  if (
                    !window.confirm(
                      `Перенести «${equipmentTypeBusinessLabel(row)}» в архив?`,
                    )
                  ) {
                    return;
                  }
                  const targetId = row.id;
                  const expected = row.version;
                  const seq = loadSeq.current;
                  setArchiveBusy(true);
                  void props.api
                    .archiveEquipmentType(targetId, expected)
                    .then((next) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      apply(next);
                      setArchiveBusy(false);
                    })
                    .catch((failure: unknown) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      setArchiveBusy(false);
                      setError(failure);
                    });
                }
              : undefined
          }
        />
      </form>

      <section className="pkg-b-fieldset" aria-labelledby="eq-package-summary-title">
        <h3 id="eq-package-summary-title">Пакет чек-листов</h3>
        {currentRevision === null ? (
          <p className="meta">Не настроено</p>
        ) : (
          <>
            <p className="meta">{packageRevisionBusinessLabel(currentRevision)}</p>
            <ul className="pkg-b-mini-list">
              {(currentRevision.items ?? []).map((item) => (
                <li key={`${item.checklist_template_id}-${item.ordinal}`}>
                  {item.ordinal}. {item.template_name}
                </li>
              ))}
            </ul>
          </>
        )}
        <button
          type="button"
          onClick={() =>
            guardedNavigate(`/admin/reference-data/equipment-types/${row.id}/package`)
          }
        >
          Управлять пакетом
        </button>
      </section>
    </div>
  );
}

type PackageAssessmentBinding = {
  readonly equipmentTypeId: string;
  readonly revisionId: string;
  readonly impactMode: ConfigurationImpactMode;
  readonly expectedVersion: string;
  readonly generation: number;
};

function assessmentMatchesBinding(
  assessment: ConfigurationImpactAssessment,
  binding: PackageAssessmentBinding,
): boolean {
  if (assessment.target_root_id !== binding.equipmentTypeId) {
    return false;
  }
  if (assessment.target_revision_or_version_id !== binding.revisionId) {
    return false;
  }
  if (String(assessment.impact_mode) !== binding.impactMode) {
    return false;
  }
  if (assessment.expected_root_version !== binding.expectedVersion) {
    return false;
  }
  if (
    typeof assessment.expires_at === 'string' &&
    assessment.expires_at.trim() !== '' &&
    Number.isFinite(Date.parse(assessment.expires_at)) &&
    Date.parse(assessment.expires_at) <= Date.now()
  ) {
    return false;
  }
  return true;
}

function EquipmentPackagePage(props: {
  readonly api: AdminApi;
  readonly equipmentTypeId: string;
}): ReactElement {
  const [equipmentType, setEquipmentType] = useState<EquipmentTypeAdmin | null>(null);
  const [revisions, setRevisions] = useState<EquipmentPackageRevision[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplateAdmin[]>([]);
  const [orderedTemplateIds, setOrderedTemplateIds] = useState<string[]>([]);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
  const [impactMode, setImpactMode] = useState<ConfigurationImpactMode>('STANDARD');
  const [assessment, setAssessment] = useState<ConfigurationImpactAssessment | null>(null);
  const [assessmentBinding, setAssessmentBinding] = useState<PackageAssessmentBinding | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const assessSeq = useRef(0);
  const activateSeq = useRef(0);
  const activateInFlight = useRef(false);
  const liveRef = useRef({
    selectedRevisionId: null as string | null,
    impactMode: 'STANDARD' as ConfigurationImpactMode,
    equipmentType: null as EquipmentTypeAdmin | null,
  });
  liveRef.current = { selectedRevisionId, impactMode, equipmentType };
  const dirty = orderedTemplateIds.length > 0;
  useUnsavedLeaveGuard(dirty && !busy);

  const selectedRevision = useMemo(
    () => revisions.find((row) => row.id === selectedRevisionId) ?? null,
    [revisions, selectedRevisionId],
  );
  const orderedTemplates = useMemo(
    () =>
      orderedTemplateIds
        .map((id) => templates.find((row) => row.id === id))
        .filter((row): row is ChecklistTemplateAdmin => row !== undefined),
    [orderedTemplateIds, templates],
  );
  const availableTemplates = useMemo(
    () => templates.filter((row) => !orderedTemplateIds.includes(row.id)),
    [templates, orderedTemplateIds],
  );

  const invalidateAssessment = () => {
    assessSeq.current += 1;
    setAssessment(null);
    setAssessmentBinding(null);
    setBusy(false);
  };

  const assessmentUsable =
    assessment !== null &&
    assessmentBinding !== null &&
    equipmentType !== null &&
    selectedRevisionId !== null &&
    assessmentBinding.generation === assessSeq.current &&
    assessmentBinding.equipmentTypeId === equipmentType.id &&
    assessmentBinding.revisionId === selectedRevisionId &&
    assessmentBinding.impactMode === impactMode &&
    assessmentBinding.expectedVersion === equipmentType.version &&
    assessmentMatchesBinding(assessment, assessmentBinding);

  const reload = () => {
    setLoading(true);
    void Promise.all([
      props.api.getEquipmentType(props.equipmentTypeId),
      props.api.listEquipmentPackageRevisions(props.equipmentTypeId),
      props.api.listChecklistTemplates(),
    ])
      .then(([equipment, revisionRows, templateRows]) => {
        setEquipmentType(equipment);
        const list = Array.isArray(revisionRows) ? revisionRows : [];
        setRevisions(list);
        setTemplates(Array.isArray(templateRows) ? templateRows : []);
        const current = list.find((row) => row.is_current) ?? list[0] ?? null;
        setSelectedRevisionId((prev) => prev ?? current?.id ?? null);
        setError(null);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.equipmentTypeId]);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (equipmentType === null) {
    return error !== null ? <ProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() =>
          guardedNavigate(`/admin/reference-data/equipment-types/${equipmentType.id}`)
        }
      >
        ← К карточке вида техники
      </button>
      <h2>Пакет чек-листов</h2>
      <p className="meta">{equipmentTypeBusinessLabel(equipmentType)}</p>
      {error !== null ? <ProblemAlert failure={error} /> : null}

      <section className="pkg-b-fieldset">
        <h3>История версий</h3>
        {revisions.length === 0 ? (
          <p role="status">Версии пакета ещё не созданы.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table pkg-b-table">
              <thead>
                <tr>
                  <th>Версия</th>
                  <th>Чек-листы</th>
                  <th>Состояние</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((row) => (
                  <tr key={row.id}>
                    <td>{packageRevisionBusinessLabel(row)}</td>
                    <td>{(row.items ?? []).map((item) => item.template_name).join(', ')}</td>
                    <td>{row.is_current ? 'Активная' : 'Неактивная'}</td>
                    <td>
                      <button
                        type="button"
                        className="pkg-b-registry-link"
                        onClick={() => {
                          setSelectedRevisionId(row.id);
                          invalidateAssessment();
                        }}
                      >
                        Выбрать
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedRevision !== null ? (
        <section className="pkg-b-fieldset">
          <h3>{packageRevisionBusinessLabel(selectedRevision)}</h3>
          <ul className="pkg-b-mini-list">
            {(selectedRevision.items ?? []).map((item) => (
              <li key={`${item.checklist_template_id}-${item.ordinal}`}>
                {item.ordinal}. {item.template_name}
              </li>
            ))}
          </ul>
          <label htmlFor="impact_mode">
            Режим воздействия
            <select
              id="impact_mode"
              aria-label="Режим воздействия пакета"
              value={impactMode}
              onChange={(event) => {
                setImpactMode(event.target.value as ConfigurationImpactMode);
                invalidateAssessment();
              }}
            >
              <option value="STANDARD">{impactModeLabel('STANDARD')}</option>
              <option value="MANDATORY_PRESTART">{impactModeLabel('MANDATORY_PRESTART')}</option>
            </select>
          </label>
          <div className="pkg-b-form-actions">
            <button
              type="button"
              disabled={busy || selectedRevision.is_current}
              onClick={() => {
                const binding: PackageAssessmentBinding = {
                  equipmentTypeId: equipmentType.id,
                  revisionId: selectedRevision.id,
                  impactMode,
                  expectedVersion: equipmentType.version,
                  generation: ++assessSeq.current,
                };
                setAssessment(null);
                setAssessmentBinding(null);
                setBusy(true);
                setError(null);
                void props.api
                  .assessEquipmentPackageActivationImpact(
                    binding.equipmentTypeId,
                    binding.revisionId,
                    {
                      expected_equipment_type_version: binding.expectedVersion,
                      impact_mode: binding.impactMode,
                    },
                  )
                  .then((next) => {
                    if (binding.generation !== assessSeq.current) {
                      return;
                    }
                    const live = liveRef.current;
                    if (
                      live.equipmentType === null ||
                      live.equipmentType.id !== binding.equipmentTypeId ||
                      live.selectedRevisionId !== binding.revisionId ||
                      live.impactMode !== binding.impactMode ||
                      live.equipmentType.version !== binding.expectedVersion
                    ) {
                      setBusy(false);
                      return;
                    }
                    if (!assessmentMatchesBinding(next, binding)) {
                      setBusy(false);
                      setError(
                        new Error(
                          'Ответ оценки воздействия не соответствует текущему контексту. Повторите проверку.',
                        ),
                      );
                      return;
                    }
                    setAssessment(next);
                    setAssessmentBinding(binding);
                    setBusy(false);
                  })
                  .catch((failure: unknown) => {
                    if (binding.generation !== assessSeq.current) {
                      return;
                    }
                    setError(failure);
                    setBusy(false);
                  });
              }}
            >
              Проверить влияние изменений
            </button>
            <button
              type="button"
              disabled={busy || !assessmentUsable || selectedRevision.is_current}
              onClick={() => {
                if (
                  busy ||
                  activateInFlight.current ||
                  !assessmentUsable ||
                  assessment === null ||
                  assessmentBinding === null
                ) {
                  return;
                }
                if (
                  !window.confirm(
                    `Сделать «${packageRevisionBusinessLabel(selectedRevision)}» активной?`,
                  )
                ) {
                  return;
                }
                const seq = ++activateSeq.current;
                const activateBinding = assessmentBinding;
                const activateAssessmentId = assessment.id;
                activateInFlight.current = true;
                setBusy(true);
                setError(null);
                void props.api
                  .activateEquipmentPackageRevision(
                    activateBinding.equipmentTypeId,
                    activateBinding.revisionId,
                    {
                      expected_equipment_type_version: activateBinding.expectedVersion,
                      impact_assessment_id: activateAssessmentId,
                      impact_mode: activateBinding.impactMode,
                    },
                  )
                  .then((next) => {
                    if (seq !== activateSeq.current) {
                      return;
                    }
                    setEquipmentType(next);
                    activateInFlight.current = false;
                    invalidateAssessment();
                    setBusy(false);
                    reload();
                  })
                  .catch((failure: unknown) => {
                    if (seq !== activateSeq.current) {
                      return;
                    }
                    activateInFlight.current = false;
                    // Keep draft/selection/assessment binding; no silent overwrite on STALE_VERSION.
                    if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                      setError(failure);
                      setBusy(false);
                      return;
                    }
                    setError(failure);
                    setBusy(false);
                  });
              }}
            >
              Сделать версию активной
            </button>
          </div>
          {assessmentUsable && assessment !== null ? (
            <div role="status" className="banner banner-open">
              <p>Оценка воздействия · {impactModeLabel(assessment.impact_mode)}</p>
              <p>{impactAssessmentSummary(assessment)}</p>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="pkg-b-fieldset">
        <h3>Подготовка новой версии</h3>
        <p className="meta">
          Создание версии не активирует её. Активация — отдельное подтверждение после проверки
          влияния.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (busy) {
              return;
            }
            if (orderedTemplateIds.length === 0) {
              setError('Выберите хотя бы один чек-лист.');
              return;
            }
            setBusy(true);
            setError(null);
            void props.api
              .createEquipmentPackageRevision(equipmentType.id, {
                expected_equipment_type_version: equipmentType.version,
                items: orderedTemplateIds.map((id, index) => ({
                  checklist_template_id: id,
                  ordinal: index + 1,
                })),
              })
              .then((created) => {
                setOrderedTemplateIds([]);
                invalidateAssessment();
                setSelectedRevisionId(created.id);
                setBusy(false);
                reload();
              })
              .catch((failure: unknown) => {
                setError(failure);
                setBusy(false);
              });
          }}
        >
          <div className="stack-gap" aria-label="Выбранные чек-листы пакета">
            {orderedTemplates.length === 0 ? (
              <p className="meta" role="status">
                Пока ничего не выбрано.
              </p>
            ) : null}
            {orderedTemplates.map((template, index) => (
              <div key={template.id} className="pkg-b-field-card">
                <p>
                  {index + 1}. {templateBusinessLabel(template)}
                </p>
                <div className="pkg-b-form-actions">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => {
                      const next = [...orderedTemplateIds];
                      const [id] = next.splice(index, 1);
                      next.splice(index - 1, 0, id!);
                      setOrderedTemplateIds(next);
                      invalidateAssessment();
                    }}
                  >
                    Вверх
                  </button>
                  <button
                    type="button"
                    disabled={index === orderedTemplates.length - 1}
                    onClick={() => {
                      const next = [...orderedTemplateIds];
                      const [id] = next.splice(index, 1);
                      next.splice(index + 1, 0, id!);
                      setOrderedTemplateIds(next);
                      invalidateAssessment();
                    }}
                  >
                    Вниз
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setOrderedTemplateIds(
                        orderedTemplateIds.filter((id) => id !== template.id),
                      );
                      invalidateAssessment();
                    }}
                  >
                    Убрать
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="stack-gap" aria-label="Доступные чек-листы">
            {availableTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setOrderedTemplateIds([...orderedTemplateIds, template.id]);
                  invalidateAssessment();
                }}
              >
                Добавить: {templateBusinessLabel(template)}
              </button>
            ))}
          </div>
          <div className="pkg-b-form-actions">
            <button type="submit" disabled={busy}>
              Создать новую версию пакета
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Inspection sites                                                           */
/* -------------------------------------------------------------------------- */

function InspectionSiteList(props: { readonly api: AdminApi }): ReactElement {
  const session = filterSessions['inspection-sites'];
  const [draft, setDraft] = useState<FilterState>(session.draft);
  const [applied, setApplied] = useState<FilterState>(session.applied);
  const [rows, setRows] = useState<InspectionSiteAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [page, setPage] = useState(1);

  const reload = () => {
    setLoading(true);
    void props.api
      .listInspectionSites()
      .then((items) => {
        setRows(Array.isArray(items) ? items : []);
        setError(null);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        setError(failure);
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const filtered = rows
    .filter((row) => {
      if (applied.status !== 'ALL' && row.status !== applied.status) {
        return false;
      }
      if (applied.query.trim().length === 0) {
        return true;
      }
      return (
        matchIncludes(row.display_name, applied.query) ||
        matchIncludes(row.description ?? '', applied.query)
      );
    })
    .slice()
    .sort((a, b) => {
      const byOrder = a.display_order - b.display_order;
      return byOrder !== 0 ? byOrder : compareRu(a.display_name, b.display_name);
    });
  const { pageRows, totalPages } = paginate(filtered, page);

  return (
    <div className="pkg-b-section">
      <div className="pkg-b-section-head">
        <h2>Места / площадки осмотра</h2>
        <button
          type="button"
          className="pkg-b-create"
          onClick={() => guardedNavigate('/admin/reference-data/inspection-sites/new')}
        >
          Создать площадку
        </button>
      </div>
      <FilterBar
        section="inspection-sites"
        draft={draft}
        onDraft={(next) => {
          session.draft = next;
          setDraft(next);
        }}
        onApply={() => {
          session.applied = draft;
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          const empty: FilterState = { query: '', status: 'ALL' };
          session.draft = empty;
          session.applied = empty;
          setDraft(empty);
          setApplied(empty);
          setPage(1);
        }}
      />
      <RegistryStates
        loading={loading}
        error={error}
        empty={!loading && error === null && rows.length === 0}
        filteredEmpty={!loading && error === null && rows.length > 0 && filtered.length === 0}
        onRetry={reload}
      />
      {!loading && error === null && filtered.length > 0 ? (
        <>
          <p className="meta pkg-b-tablebar">
            Показано {pageRows.length} из {filtered.length}
          </p>
          <div className="table-wrap">
            <table className="data-table pkg-b-table">
              <thead>
                <tr>
                  <th scope="col">Площадка</th>
                  <th scope="col">Порядок</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Действие</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <CatalogLink href={`/admin/reference-data/inspection-sites/${row.id}`}>
                        {inspectionSiteBusinessLabel(row)}
                      </CatalogLink>
                    </td>
                    <td>{row.display_order}</td>
                    <td>
                      <StatusChip
                        label={directoryStatusLabel(row.status)}
                        tone={statusToneClass(row.status)}
                      />
                    </td>
                    <td>
                      <CatalogLink href={`/admin/reference-data/inspection-sites/${row.id}`}>
                        Открыть
                      </CatalogLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="pkg-b-pager">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Назад
              </button>
              <span className="meta">
                Стр. {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      <p className="meta pkg-b-hint">
        Расписание площадок настраивается отдельно в разделе «Расписание площадок».
      </p>
    </div>
  );
}

function InspectionSiteCreate(props: { readonly api: AdminApi }): ReactElement {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [existing, setExisting] = useState<InspectionSiteAdmin[]>([]);
  useUnsavedLeaveGuard(
    (displayName.trim().length > 0 || description.trim().length > 0) && !busy,
  );

  useEffect(() => {
    void props.api
      .listInspectionSites()
      .then((rows) => setExisting(Array.isArray(rows) ? rows : []))
      .catch(() => setExisting([]));
  }, [props.api]);

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/inspection-sites')}
      >
        ← К площадкам
      </button>
      <h2>Новая площадка</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          setBusy(true);
          setError(null);
          void props.api
            .createInspectionSite({
              code: generateInternalCode('InspectionSite'),
              display_name: displayName.trim(),
              description: description.trim().length === 0 ? null : description.trim(),
              display_order: nextDisplayOrder(existing),
            })
            .then((row) => {
              setBusy(false);
              navigate(`/admin/reference-data/inspection-sites/${row.id}`);
            })
            .catch((failure: unknown) => {
              setError(failure);
              setBusy(false);
            });
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="site_create_name">
            Название площадки
            <input
              id="site_create_name"
              aria-label="Название площадки"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label htmlFor="site_create_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="site_create_desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>
        </div>
        <FormActions
          primaryLabel="Создать"
          busy={busy}
          onCancel={() => guardedNavigate('/admin/reference-data/inspection-sites')}
        />
      </form>
    </div>
  );
}

function InspectionSiteDetail(props: {
  readonly api: AdminApi;
  readonly inspectionSiteId: string;
}): ReactElement {
  const [row, setRow] = useState<InspectionSiteAdmin | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [baseline, setBaseline] = useState<{
    name: string;
    description: string;
    order: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const loadSeq = useRef(0);

  const apply = (next: InspectionSiteAdmin) => {
    setRow(next);
    setDisplayName(next.display_name);
    setDescription(next.description ?? '');
    setDisplayOrder(String(next.display_order));
    setBaseline({
      name: next.display_name,
      description: next.description ?? '',
      order: String(next.display_order),
    });
    setStale(false);
  };

  const reload = () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    void props.api
      .getInspectionSite(props.inspectionSiteId)
      .then((next) => {
        if (loadSeq.current !== seq) {
          return;
        }
        apply(next);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        if (failure instanceof ApiError && failure.status === 404) {
          setNotFound(true);
        } else {
          setError(failure);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.inspectionSiteId]);

  const dirty =
    baseline !== null &&
    (displayName !== baseline.name ||
      description !== baseline.description ||
      displayOrder !== baseline.order);
  useUnsavedLeaveGuard(dirty && !busy);

  if (loading) {
    return <p role="status">Загрузка…</p>;
  }
  if (notFound) {
    return (
      <div role="alert">
        <p>Запись не найдена</p>
        <button
          type="button"
          className="back-link"
          onClick={() => navigate('/admin/reference-data/inspection-sites')}
        >
          К списку
        </button>
      </div>
    );
  }
  if (row === null) {
    return error !== null ? <ProblemAlert failure={error} /> : <p role="status">Загрузка…</p>;
  }

  return (
    <div className="pkg-b-section">
      <button
        type="button"
        className="back-link"
        onClick={() => guardedNavigate('/admin/reference-data/inspection-sites')}
      >
        ← К площадкам
      </button>
      <div className="pkg-b-detail-header">
        <h2>{inspectionSiteBusinessLabel(row)}</h2>
        <StatusChip label={directoryStatusLabel(row.status)} tone={statusToneClass(row.status)} />
      </div>
      {stale ? (
        <div role="alert" className="banner banner-closed">
          <p>{STALE_UI_MESSAGE}</p>
          <button type="button" onClick={reload}>
            Перезагрузить актуальное
          </button>
        </div>
      ) : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-b-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          const targetId = row.id;
          const expected = row.version;
          const seq = loadSeq.current;
          setBusy(true);
          setError(null);
          void props.api
            .updateInspectionSite(targetId, {
              expected_version: expected,
              display_name: displayName.trim(),
              description: description.trim().length === 0 ? null : description.trim(),
              display_order: Number(displayOrder),
            })
            .then((next) => {
              if (loadSeq.current !== seq) {
                return;
              }
              apply(next);
              setBusy(false);
            })
            .catch((failure: unknown) => {
              if (loadSeq.current !== seq) {
                return;
              }
              setBusy(false);
              if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
                setStale(true);
              }
              setError(failure);
            });
        }}
      >
        <div className="pkg-b-form-grid">
          <label htmlFor="site_edit_name">
            Название площадки для сохранения
            <input
              id="site_edit_name"
              aria-label="Название площадки для сохранения"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label htmlFor="site_edit_order">
            Порядок отображения
            <input
              id="site_edit_order"
              aria-label="Порядок площадки для сохранения"
              type="number"
              min={1}
              value={displayOrder}
              onChange={(event) => setDisplayOrder(event.target.value)}
              required
            />
          </label>
          <label htmlFor="site_edit_desc" className="pkg-b-span-2">
            Описание
            <textarea
              id="site_edit_desc"
              aria-label="Описание площадки"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </label>
        </div>
        <FormActions
          primaryLabel="Сохранить"
          busy={busy}
          onCancel={() => {
            if (baseline === null) {
              return;
            }
            if (dirty && !window.confirm(getUnsavedLeaveMessage())) {
              return;
            }
            setDisplayName(baseline.name);
            setDescription(baseline.description);
            setDisplayOrder(baseline.order);
            setError(null);
          }}
          archiveLabel={row.status === 'ACTIVE' ? 'В архив' : undefined}
          archiveBusy={archiveBusy}
          onArchive={
            row.status === 'ACTIVE'
              ? () => {
                  if (!window.confirm(`Перенести «${inspectionSiteBusinessLabel(row)}» в архив?`)) {
                    return;
                  }
                  const targetId = row.id;
                  const expected = row.version;
                  const seq = loadSeq.current;
                  setArchiveBusy(true);
                  void props.api
                    .archiveInspectionSite(targetId, expected)
                    .then((next) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      apply(next);
                      setArchiveBusy(false);
                    })
                    .catch((failure: unknown) => {
                      if (loadSeq.current !== seq) {
                        return;
                      }
                      setArchiveBusy(false);
                      setError(failure);
                    });
                }
              : undefined
          }
        />
      </form>
      <p className="meta pkg-b-hint">
        Расписание площадок настраивается отдельно в разделе «Расписание площадок».
      </p>
    </div>
  );
}
